import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, MapPinned, PencilLine, Plus, RefreshCw, RotateCcw, Save, Search, Trash2 } from 'lucide-react';
import { fetchKendamaMapPoints, saveKendamaMapPoints, submitKendamaMapSuggestion } from '../kendamaMapStorage';
import { KendamaMapPoint, KendamaMapPointGroup, KendamaMapPointLogoBackground, User } from '../types';

interface KendamaMapModeProps {
  onBack: () => void;
  currentUser: User | null;
}

type AMapPlaceSearchPoi = {
  id?: string;
  name?: string;
  address?: string | string[];
  location?: { lng?: number; lat?: number } | null;
};

type AMapPlaceSearchResult = {
  poiList?: { pois?: AMapPlaceSearchPoi[] } | null;
};

type AMapPlaceSearchInstance = {
  search: (keyword: string, callback: (searchStatus: string, result: AMapPlaceSearchResult | null) => void) => void;
};

type AMapLike = {
  Map: new (container: HTMLElement, options?: Record<string, unknown>) => AMapMapInstance;
  Marker: new (options?: Record<string, unknown>) => AMapMarkerInstance;
  Pixel: new (x: number, y: number) => unknown;
  // 地点搜索插件：先 plugin 加载，加载完成后才能 new PlaceSearch。
  plugin: (names: string | string[], callback: () => void) => void;
  PlaceSearch: new (options?: Record<string, unknown>) => AMapPlaceSearchInstance;
};

type AMapMapInstance = {
  add: (overlays: unknown[] | unknown) => void;
  remove: (overlays: unknown[] | unknown) => void;
  setFitView: (overlays?: unknown[], immediately?: boolean, avoid?: number[]) => void;
  setZoomAndCenter: (zoom: number, center: [number, number]) => void;
  on: (eventName: string, handler: (event?: any) => void) => void;
  destroy: () => void;
};

type AMapMarkerInstance = {
  on: (eventName: string, handler: () => void) => void;
  getPosition: () => unknown;
};

type PointDraft = {
  name: string;
  description: string;
  logoImageUrl: string;
  logoBackground: KendamaMapPointLogoBackground;
};

type PointSuggestionDraft = {
  address: string;
  name: string;
  contact: string;
  group: KendamaMapPointGroup;
};

// 管理员搜索地图地点后的结果项。
type MapSearchResult = {
  id: string;
  name: string;
  address: string;
  lng: number;
  lat: number;
};

// 搜索框四周的模糊过渡层样式：四周留出一圈羽化带，让地图在搜索框边缘柔和淡出。
const SEARCH_BOX_BLUR_MASK_STYLE: React.CSSProperties = {
  maskImage:
    'linear-gradient(to right, rgba(0,0,0,0) 0, #000 8px, #000 calc(100% - 8px), rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,0) 0, #000 8px, #000 calc(100% - 8px), rgba(0,0,0,0) 100%)',
  maskComposite: 'intersect',
  WebkitMaskImage:
    'linear-gradient(to right, rgba(0,0,0,0) 0, #000 8px, #000 calc(100% - 8px), rgba(0,0,0,0) 100%), linear-gradient(to bottom, rgba(0,0,0,0) 0, #000 8px, #000 calc(100% - 8px), rgba(0,0,0,0) 100%)',
  WebkitMaskComposite: 'source-in',
};

// 底部抽屉的吸附档位：peek 只露出搜索框，half 露出半个地图，full 展开列表。
type SheetSnapTarget = 'peek' | 'half' | 'full';

declare global {
  interface Window {
    AMap?: AMapLike;
    _AMapSecurityConfig?: {
      securityJsCode?: string;
    };
    __amapScriptLoadingPromise?: Promise<AMapLike>;
  }
}

const getHashQuery = () => {
  if (typeof window === 'undefined') return new URLSearchParams();

  const hash = window.location.hash || '';
  const query = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  return new URLSearchParams(query);
};

const clonePoints = (points: KendamaMapPoint[]) => points.map((point) => ({ ...point }));

const createDraftFromPoint = (point: KendamaMapPoint | null): PointDraft => ({
  name: point?.name || '',
  description: point?.description || '',
  logoImageUrl: point?.logo?.imageUrl || '',
  logoBackground: point?.logo?.background || 'black',
});

const POINT_GROUP_META: Record<
  KendamaMapPointGroup,
  {
    label: string;
    markerColor: string;
    markerGlow: string;
    chipClassName: string;
    activeChipClassName: string;
  }
> = {
  practice: {
    label: '团练地点',
    markerColor: '#60a5fa',
    markerGlow: 'rgba(96, 165, 250, 0.35)',
    chipClassName: 'border-blue-400/25 bg-blue-500/10 text-blue-200',
    activeChipClassName: 'border-blue-300/60 bg-blue-500/20 text-blue-100 shadow-[0_0_14px_rgba(96,165,250,0.24)]',
  },
  club: {
    label: '俱乐部',
    markerColor: '#e879f9',
    markerGlow: 'rgba(232, 121, 249, 0.32)',
    chipClassName: 'border-fuchsia-400/25 bg-fuchsia-500/10 text-fuchsia-200',
    activeChipClassName: 'border-fuchsia-300/60 bg-fuchsia-500/20 text-fuchsia-100 shadow-[0_0_14px_rgba(232,121,249,0.24)]',
  },
  brand: {
    label: '剑玉品牌',
    markerColor: '#a3e635',
    markerGlow: 'rgba(163, 230, 53, 0.32)',
    chipClassName: 'border-lime-400/25 bg-lime-500/10 text-lime-200',
    activeChipClassName: 'border-lime-300/60 bg-lime-500/20 text-lime-100 shadow-[0_0_14px_rgba(163,230,53,0.2)]',
  },
};

const POINT_GROUP_ORDER: KendamaMapPointGroup[] = ['practice', 'club', 'brand'];

const getPointGroupMeta = (group: KendamaMapPointGroup) => POINT_GROUP_META[group];

const renderMarkerContent = (group: KendamaMapPointGroup) => {
  const meta = getPointGroupMeta(group);
  return `
    <div style="position:relative;width:18px;height:18px;border-radius:9999px;background:${meta.markerColor};border:2px solid rgba(255,255,255,0.96);box-shadow:0 0 0 4px ${meta.markerGlow}, 0 0 16px ${meta.markerGlow};">
      <div style="position:absolute;left:50%;top:50%;width:4px;height:4px;border-radius:9999px;background:rgba(255,255,255,0.95);transform:translate(-50%,-50%);"></div>
    </div>
  `;
};

const MAX_POINT_LOGO_FILE_SIZE = 1024 * 1024;

const getPointLogoSurfaceClassName = (background: KendamaMapPointLogoBackground) =>
  background === 'white'
    ? 'border-zinc-200/80 bg-white'
    : 'border-white/10 bg-zinc-950';

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Logo 读取失败，请重试。'));
    };
    reader.onerror = () => reject(new Error('Logo 读取失败，请重试。'));
    reader.readAsDataURL(file);
  });

const loadAmapScript = (apiKey: string, securityJsCode?: string) => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('AMap can only be loaded in the browser.'));
  }

  if (window.AMap) {
    return Promise.resolve(window.AMap);
  }

  if (window.__amapScriptLoadingPromise) {
    return window.__amapScriptLoadingPromise;
  }

  if (securityJsCode) {
    window._AMapSecurityConfig = { securityJsCode };
  }

  window.__amapScriptLoadingPromise = new Promise<AMapLike>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-amap-jsapi="2.0"]');
    const handleLoad = () => {
      if (window.AMap) {
        resolve(window.AMap);
        return;
      }

      window.__amapScriptLoadingPromise = undefined;
      reject(new Error('AMap JS API loaded, but AMap is unavailable on window.'));
    };

    const handleError = () => {
      window.__amapScriptLoadingPromise = undefined;
      reject(new Error('Failed to load AMap JS API 2.0 script.'));
    };

    if (existingScript) {
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.amapJsapi = '2.0';
    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });

  return window.__amapScriptLoadingPromise;
};

export const KendamaMapMode: React.FC<KendamaMapModeProps> = ({ onBack, currentUser }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<AMapMapInstance | null>(null);
  const markersRef = useRef<AMapMarkerInstance[]>([]);
  const isAdminRef = useRef(false);
  const isAddModeRef = useRef(false);
  const sheetDragRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const sheetSnapPointsRef = useRef({ peek: 0, half: 0, full: 0 });
  const topBarRef = useRef<HTMLDivElement | null>(null);
  const floatingOverlayRef = useRef<HTMLDivElement | null>(null);

  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [points, setPoints] = useState<KendamaMapPoint[]>([]);
  const [savedPoints, setSavedPoints] = useState<KendamaMapPoint[]>([]);
  const [selectedPointId, setSelectedPointId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PointDraft>({
    name: '',
    description: '',
    logoImageUrl: '',
    logoBackground: 'black',
  });
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isPointCardOpen, setIsPointCardOpen] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [amapApi, setAmapApi] = useState<AMapLike | null>(null);
  const [pointSearch, setPointSearch] = useState('');
  const [mapSearchKeyword, setMapSearchKeyword] = useState('');
  const [mapSearchResults, setMapSearchResults] = useState<MapSearchResult[]>([]);
  const [mapSearchMessage, setMapSearchMessage] = useState('');
  const [isMapSearchLoading, setIsMapSearchLoading] = useState(false);
  const [pointGroupFilter, setPointGroupFilter] = useState<KendamaMapPointGroup | null>(null);
  const [isSuggestionCardOpen, setIsSuggestionCardOpen] = useState(false);
  const [isSuggestionSubmitting, setIsSuggestionSubmitting] = useState(false);
  const [suggestionMessage, setSuggestionMessage] = useState('');
  const [suggestionDraft, setSuggestionDraft] = useState<PointSuggestionDraft>({
    address: '',
    name: '',
    contact: '',
    group: 'practice',
  });
  const [viewportHeight, setViewportHeight] = useState(0);
  const [sheetHeight, setSheetHeight] = useState<number | null>(null);
  const [isSheetDragging, setIsSheetDragging] = useState(false);
  // 抽屉上方要为顶部控件与悬浮搜索框留出的高度，避免抽屉拉满时把搜索框顶到标题栏里。
  const [sheetReservedSpace, setSheetReservedSpace] = useState(0);

  // 抽屉三档吸附高度按容器高度换算，保证小屏与大屏都合理。
  const sheetSnapPoints = useMemo(() => {
    const height = viewportHeight > 0 ? viewportHeight : 720;
    const peek = Math.min(212, Math.max(150, Math.round(height * 0.32)));
    const half = Math.max(peek, Math.round(height * 0.5));
    const maxFull = Math.round(height * 0.88);
    const full = Math.max(half, sheetReservedSpace > 0 ? Math.min(maxFull, height - sheetReservedSpace) : maxFull);
    return { peek, half, full };
  }, [viewportHeight, sheetReservedSpace]);

  const currentSheetHeight = sheetHeight ?? sheetSnapPoints.half;

  useEffect(() => {
    sheetSnapPointsRef.current = sheetSnapPoints;
  }, [sheetSnapPoints]);

  const snapSheetTo = useCallback(
    (target: SheetSnapTarget) => {
      setSheetHeight(sheetSnapPoints[target]);
    },
    [sheetSnapPoints],
  );

  useEffect(() => {
    const element = rootRef.current;
    if (!element) return;

    const updateHeight = () => {
      setViewportHeight(element.clientHeight);

      // 悬浮控件列贴在抽屉上方，抽屉拉满时不能盖住顶部控件，故按实际高度预留空间。
      const rootTop = element.getBoundingClientRect().top;
      const topBar = topBarRef.current;
      const overlay = floatingOverlayRef.current;
      const topBarBottom = topBar ? topBar.getBoundingClientRect().bottom - rootTop : 96;
      const overlayHeight = overlay ? overlay.offsetHeight : 120;
      setSheetReservedSpace(Math.round(topBarBottom + overlayHeight + 20));
    };
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleSheetPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    sheetDragRef.current = { startY: event.clientY, startHeight: currentSheetHeight };
    setIsSheetDragging(true);
  };

  const handleSheetPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = sheetDragRef.current;
    if (!drag) return;
    const next = drag.startHeight + (drag.startY - event.clientY);
    setSheetHeight(Math.min(sheetSnapPoints.full, Math.max(sheetSnapPoints.peek, next)));
  };

  const handleSheetPointerUp = () => {
    const drag = sheetDragRef.current;
    if (!drag) return;
    sheetDragRef.current = null;
    setIsSheetDragging(false);

    const candidates: number[] = [sheetSnapPoints.peek, sheetSnapPoints.half, sheetSnapPoints.full];
    const current = sheetHeight ?? sheetSnapPoints.half;
    setSheetHeight(
      candidates.reduce((closest, value) => (Math.abs(value - current) < Math.abs(closest - current) ? value : closest), candidates[0]),
    );
  };

  // 抽屉停在最小档时点击搜索框，自动展开到一半档，方便输入与查看搜索结果。
  const handleSearchBoxActivate = () => {
    const current = sheetHeight ?? sheetSnapPoints.half;
    if (current <= sheetSnapPoints.peek) {
      snapSheetTo('half');
    }
  };

  // 管理员专用：搜索地图地点，用于快速把镜头移到目标位置后就近打点。
  const handleMapSearch = () => {
    const amap = amapApi;
    const keyword = mapSearchKeyword.trim();
    if (!amap || !keyword || isMapSearchLoading) return;

    setIsMapSearchLoading(true);
    setMapSearchMessage('');

    amap.plugin('AMap.PlaceSearch', () => {
      if (typeof amap.PlaceSearch !== 'function') {
        setIsMapSearchLoading(false);
        setMapSearchResults([]);
        setMapSearchMessage('地点搜索插件不可用，请稍后重试。');
        return;
      }

      const placeSearch = new amap.PlaceSearch({ pageSize: 10, pageIndex: 1 });
      placeSearch.search(keyword, (searchStatus, result) => {
        setIsMapSearchLoading(false);
        const pois = searchStatus === 'complete' ? result?.poiList?.pois ?? [] : [];
        const nextResults = pois
          .map((poi, index): MapSearchResult | null => {
            const lng = Number(poi.location?.lng);
            const lat = Number(poi.location?.lat);
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
            return {
              id: poi.id || `${poi.name || keyword}-${index}`,
              name: poi.name || keyword,
              address: Array.isArray(poi.address) ? poi.address.join('') : poi.address || '',
              lng,
              lat,
            };
          })
          .filter((item): item is MapSearchResult => item !== null);

        setMapSearchResults(nextResults);
        setMapSearchMessage(nextResults.length === 0 ? '没有找到相关地点，换个关键词试试。' : '');
      });
    });
  };

  const handleSelectMapSearchResult = (result: MapSearchResult) => {
    mapRef.current?.setZoomAndCenter(16, [result.lng, result.lat]);
    setMapSearchKeyword(result.name);
    setMapSearchResults([]);
    setMapSearchMessage('');
    // 收起抽屉露出地图，方便接着在地图上打点。
    setSheetHeight(sheetSnapPointsRef.current.peek);
  };

  const handleResetMapSearch = () => {
    setMapSearchKeyword('');
    setMapSearchResults([]);
    setMapSearchMessage('');
  };

  const hashQuery = getHashQuery();
  const apiKey = import.meta.env.VITE_AMAP_API_KEY?.trim() || hashQuery.get('amapKey')?.trim() || '';
  const securityJsCode =
    import.meta.env.VITE_AMAP_SECURITY_JS_CODE?.trim() || hashQuery.get('securityJsCode')?.trim() || '';

  const isAdmin = !!currentUser && !!(currentUser.isAdmin || currentUser.roles?.includes('administrator'));
  const selectedPoint = points.find((point) => point.id === selectedPointId) || null;
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(points) !== JSON.stringify(savedPoints),
    [points, savedPoints],
  );
  const filteredPoints = useMemo(() => {
    const keyword = pointSearch.trim().toLowerCase();
    return points.filter((point) => {
      if (pointGroupFilter && point.group !== pointGroupFilter) {
        return false;
      }
      if (!keyword) return true;
      const haystack = `${point.name} ${point.description} ${getPointGroupMeta(point.group).label}`.toLowerCase();
      return haystack.includes(keyword);
    });
  }, [pointGroupFilter, pointSearch, points]);
  const pointGroupCounts = useMemo(
    () =>
      POINT_GROUP_ORDER.map((group) => ({
        group,
        count: points.filter((point) => point.group === group).length,
      })),
    [points],
  );

  useEffect(() => {
    isAdminRef.current = isAdmin;
  }, [isAdmin]);

  useEffect(() => {
    isAddModeRef.current = isAddMode;
  }, [isAddMode]);

  useEffect(() => {
    setDraft(createDraftFromPoint(selectedPoint));
  }, [
    selectedPointId,
    selectedPoint?.name,
    selectedPoint?.lng,
    selectedPoint?.lat,
    selectedPoint?.description,
    selectedPoint?.logo?.imageUrl,
    selectedPoint?.logo?.background,
  ]);

  const loadPoints = useCallback(async (signal?: AbortSignal, forceRefresh = false): Promise<KendamaMapPoint[]> => {
    const fetchedPoints = await fetchKendamaMapPoints(signal, forceRefresh);
    // Clone once for mutable state; savedPoints uses the same reference to avoid double cloning
    const nextPoints = clonePoints(fetchedPoints);
    setPoints(nextPoints);
    setSavedPoints(nextPoints);
    setSelectedPointId(null);
    setIsPointCardOpen(false);
    return nextPoints;
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setErrorMessage('');
    setSaveMessage('');
    try {
      await loadPoints(undefined, true);
      setSaveMessage('点位已更新');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '更新点位失败，请稍后重试。';
      setErrorMessage(msg);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    setIsDataLoading(true);
    loadPoints()
      .then(() => {
        if (cancelled) return;
      })
      .catch((error: unknown) => {
        if (cancelled || (error as Error)?.name === 'AbortError') return;
        setErrorMessage(error instanceof Error ? error.message : '加载地图点位失败。');
      })
      .finally(() => {
        if (!cancelled) {
          setIsDataLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loadPoints]);

  useEffect(() => {
    if (!apiKey) {
      setStatus('error');
      setErrorMessage('未检测到高德 Key。请在 `.env` 中配置 `VITE_AMAP_API_KEY`，或用 `#/kendama-map?amapKey=你的Key` 临时传入。');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setErrorMessage('');

    loadAmapScript(apiKey, securityJsCode)
      .then((AMap) => {
        if (cancelled) return;
        setAmapApi(AMap);
        setStatus('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : '高德地图初始化失败。');
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, securityJsCode]);

  useEffect(() => {
    if (!amapApi || !mapContainerRef.current || mapRef.current) return;

    const mapInstance = new amapApi.Map(mapContainerRef.current, {
      viewMode: '2D',
      resizeEnable: true,
      zoom: 5,
      center: [points[0]?.lng ?? 116.397428, points[0]?.lat ?? 39.90923],
      mapStyle: 'amap://styles/darkblue',
    });

    mapInstance.on('click', (event?: any) => {
      // 点击地图即把抽屉收到最小档，露出完整地图。
      setSheetHeight(sheetSnapPointsRef.current.peek);

      if (!isAdminRef.current || !isAddModeRef.current) return;

      const lng = Number(event?.lnglat?.getLng?.() ?? event?.lnglat?.lng);
      const lat = Number(event?.lnglat?.getLat?.() ?? event?.lnglat?.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;

      const newPoint: KendamaMapPoint = {
        id: `kendama-point-${Date.now()}`,
        name: `新点位 ${points.length + 1}`,
        lng,
        lat,
        description: '',
        group: 'practice',
        updatedAt: Date.now(),
      };

      setPoints((previous) => [...previous, newPoint]);
      setSelectedPointId(newPoint.id);
      setIsPointCardOpen(true);
      setIsAddMode(false);
      setSaveMessage('已新增点位，请补充信息后点击“保存全部点位”。');
    });

    mapRef.current = mapInstance;

    return () => {
      mapInstance.destroy();
      mapRef.current = null;
      markersRef.current = [];
    };
  }, [amapApi, points.length]);

  useEffect(() => {
    if (!amapApi || !mapRef.current) return;

    if (markersRef.current.length > 0) {
      mapRef.current.remove(markersRef.current);
      markersRef.current = [];
    }

    const nextMarkers = points.map((point) => {
      const groupMeta = getPointGroupMeta(point.group);
      const marker = new amapApi.Marker({
        position: [point.lng, point.lat],
        title: point.name,
        content: renderMarkerContent(point.group),
        offset: new amapApi.Pixel(-9, -9),
        zIndex: point.id === selectedPointId ? 130 : 110,
      });

      marker.on('click', () => {
        handleSelectPoint(point.id);
      });

      return marker;
    });

    if (nextMarkers.length > 0) {
      mapRef.current.add(nextMarkers);
      mapRef.current.setFitView(nextMarkers, false, [64, 64, 64, 64]);
    }

    markersRef.current = nextMarkers;
  }, [amapApi, points]);

  const handleSelectPoint = (pointId: string) => {
    setSelectedPointId(pointId);
    setIsPointCardOpen(true);
    // 收起抽屉，让被选中的标点不被遮挡。
    snapSheetTo('peek');

    const markerIndex = points.findIndex((point) => point.id === pointId);
    const marker = markerIndex >= 0 ? markersRef.current[markerIndex] : null;
    if (marker && mapRef.current) {
      mapRef.current.setFitView([marker], false, [96, 96, 96, 96]);
    }
  };

  const handleApplyDraft = () => {
    if (!selectedPointId) return;

    const name = draft.name.trim();
    const logoImageUrl = draft.logoImageUrl.trim();

    if (!name) {
      setSaveMessage('点位名称不能为空。');
      return;
    }

    setPoints((previous) =>
      previous.map((point) =>
        point.id === selectedPointId
          ? {
              ...point,
              name,
              description: draft.description.trim(),
              logo: logoImageUrl
                ? {
                    imageUrl: logoImageUrl,
                    background: draft.logoBackground,
                  }
                : undefined,
              updatedAt: Date.now(),
            }
          : point,
      ),
    );
    setSaveMessage('已更新当前点位，记得点击“保存全部点位”。');
  };

  const handleLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setSaveMessage('请上传图片格式的 logo 文件。');
      return;
    }

    if (file.size > MAX_POINT_LOGO_FILE_SIZE) {
      setSaveMessage('Logo 文件请控制在 1MB 以内。');
      return;
    }

    try {
      const imageUrl = await readFileAsDataUrl(file);
      setDraft((previous) => ({ ...previous, logoImageUrl: imageUrl }));
      setSaveMessage('Logo 已载入预览，点击“应用到当前点位”后生效。');
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : 'Logo 上传失败，请重试。');
    }
  };

  const handleRemoveDraftLogo = () => {
    setDraft((previous) => ({ ...previous, logoImageUrl: '' }));
    setSaveMessage('已清除当前 logo 预览，点击“应用到当前点位”后生效。');
  };

  const handleMovePointToGroup = (pointId: string, group: KendamaMapPointGroup) => {
    if (!isAdminRef.current) return;

    const targetPoint = points.find((point) => point.id === pointId);
    if (!targetPoint || targetPoint.group === group) return;

    setPoints((previous) =>
      previous.map((point) =>
        point.id === pointId
          ? {
              ...point,
              group,
              updatedAt: Date.now(),
            }
          : point,
      ),
    );
    setSaveMessage(`已将“${targetPoint.name}”移动到${getPointGroupMeta(group).label}分组，记得保存全部点位。`);
  };

  const handleDeleteSelectedPoint = async () => {
    if (!selectedPointId) return;
    if (points.length <= 1) {
      setSaveMessage('至少保留一个有效点位。');
      return;
    }

    const nextPoints = points.filter((point) => point.id !== selectedPointId);
    setIsSaving(true);
    setSaveMessage('');

    try {
      const persistedPoints = await saveKendamaMapPoints(nextPoints);
      const next = clonePoints(persistedPoints);
      setPoints(next);
      setSavedPoints(clonePoints(next));
      setSelectedPointId(next[0]?.id ?? null);
      setIsPointCardOpen(false);
      setSaveMessage('点位已删除并保存。');
    } catch (error) {
      setPoints(nextPoints);
      setSelectedPointId(nextPoints[0]?.id ?? null);
      setIsPointCardOpen(false);
      setSaveMessage(error instanceof Error ? error.message : '删除地图点位失败。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetChanges = () => {
    const nextPoints = clonePoints(savedPoints);
    setPoints(nextPoints);
    setSelectedPointId(null);
    setIsPointCardOpen(false);
    setIsAddMode(false);
    setSaveMessage('已恢复到最近一次保存的地图点位。');
  };

  const handleSaveAllPoints = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const persistedPoints = await saveKendamaMapPoints(points);
      const nextPoints = clonePoints(persistedPoints);
      setPoints(nextPoints);
      setSavedPoints(clonePoints(nextPoints));
      setSelectedPointId((previousId) => previousId && nextPoints.some((point) => point.id === previousId) ? previousId : null);
      setSaveMessage('地图点位已保存。');
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : '保存地图点位失败。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitSuggestion = async () => {
    const address = suggestionDraft.address.trim();
    const name = suggestionDraft.name.trim();
    const contact = suggestionDraft.contact.trim();

    if (!address || !name) {
      setSuggestionMessage('请填写想要标点的具体地址和标点名字。');
      return;
    }

    setIsSuggestionSubmitting(true);
    setSuggestionMessage('');

    try {
      const result = await submitKendamaMapSuggestion({
        address,
        name,
        contact,
        group: suggestionDraft.group,
      });
      setSuggestionDraft({ address: '', name: '', contact: '', group: 'practice' });
      setSuggestionMessage(result.message || '标点申请已提交，等待管理员审核。');
      setIsSuggestionCardOpen(false);
    } catch (error) {
      setSuggestionMessage(error instanceof Error ? error.message : '提交标点申请失败。');
    } finally {
      setIsSuggestionSubmitting(false);
    }
  };

  const overlayMessage = status === 'loading'
    ? '正在加载高德地图脚本...'
    : isDataLoading
      ? '正在读取地图点位数据...'
      : errorMessage;

  // 负边距抵消 <main> 的 py、底部导航预留的 72px 与外壳的上下安全区，让地图从屏幕顶部一直铺到最底部。
  return (
    <div
      ref={rootRef}
      className="relative -mx-2 -mt-[calc(0.5rem_+_var(--safe-top))] -mb-[calc(72px_+_var(--safe-bottom))] h-[calc(100%_+88px_+_var(--safe-top)_+_var(--safe-bottom))] w-[calc(100%+1rem)] min-h-0 overflow-hidden bg-zinc-950 md:-mx-4 md:-mt-4 md:-mb-4 md:h-[calc(100%+2rem)] md:w-[calc(100%+2rem)]"
    >
      {/* 地图铺满整个背景，其余控件以悬浮层叠在其上。 */}
      <div className="absolute inset-0">
        {/* 高德会覆盖容器的 position，故外层负责定位、内层用 h-full 撑满高度。 */}
        <div ref={mapContainerRef} className="h-full min-h-0 w-full" />
      </div>

      {/* 顶部控件需让开状态栏/灵动岛安全区。 */}
      <div
        ref={topBarRef}
        className="pointer-events-none absolute inset-x-0 top-0 z-[20] flex items-center justify-between gap-2 px-3 pt-[calc(0.75rem_+_var(--safe-top))] md:px-5"
      >
        <button
          onClick={onBack}
          className="pointer-events-auto rounded-full border border-white/10 bg-black/60 p-2 text-white/70 backdrop-blur-md transition-colors hover:text-white"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="font-orbitron text-base font-black uppercase tracking-[0.28em] text-cyan-200 drop-shadow-[0_0_14px_rgba(34,211,238,0.38)] md:text-2xl">
          Kendama Map
        </h1>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing || isDataLoading}
          title="从数据库更新点位"
          className="pointer-events-auto rounded-full border border-white/10 bg-black/60 p-2 text-white/70 backdrop-blur-md transition-colors hover:text-white disabled:opacity-30"
        >
          <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* 底部抽屉：左右各留出一点边距露出地图，抓取条可拖拽，内容区可滚动。 */}
      <div
        className={`absolute inset-x-3 bottom-0 z-[15] flex flex-col overflow-hidden rounded-t-[28px] border border-white/10 bg-black/80 shadow-[0_-18px_45px_rgba(0,0,0,0.55)] backdrop-blur-xl ${
          isSheetDragging ? '' : 'transition-[height] duration-300 ease-out'
        }`}
        style={{ height: currentSheetHeight }}
      >
        <div
          onPointerDown={handleSheetPointerDown}
          onPointerMove={handleSheetPointerMove}
          onPointerUp={handleSheetPointerUp}
          onPointerCancel={handleSheetPointerUp}
          className="flex shrink-0 cursor-grab touch-none items-center justify-center pb-2 pt-3 active:cursor-grabbing"
        >
          <div className="h-1.5 w-10 rounded-full bg-white/25" />
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 pb-[calc(76px_+_var(--safe-bottom))] md:px-5 md:pb-6">
          <div className="shrink-0 space-y-2">
            <div className="text-center text-xs leading-relaxed text-zinc-500">
              没有固定场所的剑玉品牌标点均在市政府，并非品牌的确切地址
            </div>

            {(isAdmin && saveMessage) || (!isAdmin && suggestionMessage) ? (
              <div className="rounded-2xl border border-emerald-400/15 bg-emerald-500/5 px-4 py-3 text-left text-xs leading-relaxed text-emerald-100/90">
                {isAdmin ? saveMessage : suggestionMessage}
              </div>
            ) : null}
          </div>

          {isAdmin ? (
            <div className="flex shrink-0 flex-wrap gap-2 rounded-2xl border border-cyan-400/15 bg-cyan-500/5 px-4 py-3">
              <button
                onClick={() => {
                  setIsAddMode((previous) => !previous);
                  setSaveMessage('');
                }}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] transition-colors ${
                  isAddMode
                    ? 'border-green-400/60 bg-green-500/15 text-green-200'
                    : 'border-cyan-400/30 bg-cyan-500/10 text-cyan-200 hover:border-cyan-300/50 hover:bg-cyan-400/20'
                }`}
              >
                <Plus size={14} />
                {isAddMode ? '点击地图放置新点位' : '新增点位'}
              </button>
              <button
                onClick={handleSaveAllPoints}
                disabled={!hasUnsavedChanges || isSaving}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-emerald-200 transition-colors hover:border-emerald-300/50 hover:bg-emerald-400/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Save size={14} />
                {isSaving ? '保存中' : '保存全部点位'}
              </button>
              <button
                onClick={handleResetChanges}
                disabled={!hasUnsavedChanges}
                className="inline-flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-amber-200 transition-colors hover:border-amber-300/50 hover:bg-amber-400/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <RotateCcw size={14} />
                撤销未保存修改
              </button>
            </div>
          ) : null}

          <div className="shrink-0 space-y-2">
            <div className="flex items-center justify-between gap-3 px-1">
              <div className="flex items-center gap-2 text-cyan-200">
                <MapPinned size={16} />
                <span className="font-orbitron text-xs uppercase tracking-[0.16em]">标点列表</span>
              </div>
              <div className="text-[11px] text-zinc-400">{filteredPoints.length}/{points.length}</div>
            </div>

            <div className="flex flex-wrap gap-2 px-1 pb-1">
              {pointGroupCounts.map(({ group, count }) => {
                const meta = getPointGroupMeta(group);
                const isActive = pointGroupFilter === group;
                return (
                  <button
                    key={group}
                    type="button"
                    onClick={() => setPointGroupFilter((previous) => (previous === group ? null : group))}
                    aria-pressed={isActive}
                    className={`inline-flex items-center gap-2 rounded-full border px-2 py-1 text-[10px] transition-colors ${
                      isActive ? meta.activeChipClassName : meta.chipClassName
                    }`}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: meta.markerColor, boxShadow: `0 0 10px ${meta.markerGlow}` }}
                    />
                    <span>{meta.label}</span>
                    <span className="text-white/70">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="space-y-2">
              {filteredPoints.length > 0 ? (
                filteredPoints.map((point) => {
                  const isSelected = point.id === selectedPointId;
                  const pointGroupMeta = getPointGroupMeta(point.group);
                  return (
                    <div
                      key={point.id}
                      className={`rounded-xl border px-3 py-2 transition-colors ${
                        isSelected
                          ? 'border-cyan-400/40 bg-cyan-500/10'
                          : 'border-white/10 bg-black/35 hover:border-cyan-400/30 hover:bg-white/5'
                      }`}
                    >
                      <button onClick={() => handleSelectPoint(point.id)} className="w-full text-left">
                        <div className="flex items-start gap-3">
                          {point.logo ? (
                            <div
                              className={`flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl border p-2 shadow-[0_0_18px_rgba(255,255,255,0.08)] ${getPointLogoSurfaceClassName(point.logo.background)}`}
                            >
                              <img src={point.logo.imageUrl} alt={`${point.name} logo`} className="h-full w-full rounded-xl object-contain" />
                            </div>
                          ) : null}
                          <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-white">{point.name}</div>
                              <div className="mt-1 line-clamp-2 text-xs leading-relaxed text-zinc-400">
                                {point.description || '暂无说明'}
                              </div>
                            </div>
                            <div
                              className={`shrink-0 rounded-full border px-2 py-1 text-[10px] ${pointGroupMeta.chipClassName}`}
                            >
                              {pointGroupMeta.label}
                            </div>
                          </div>
                        </div>
                      </button>

                      {isAdmin ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {POINT_GROUP_ORDER.map((group) => {
                            const meta = getPointGroupMeta(group);
                            const isActive = point.group === group;
                            return (
                              <button
                                key={group}
                                onClick={() => handleMovePointToGroup(point.id, group)}
                                className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] transition-colors ${
                                  isActive ? meta.activeChipClassName : meta.chipClassName
                                }`}
                              >
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: meta.markerColor, boxShadow: `0 0 10px ${meta.markerGlow}` }}
                                />
                                <span>{meta.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })
              ) : (
                <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-4 text-center text-xs text-zinc-400">
                  未找到匹配的标点
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 悬浮在抽屉上方的控件列：搜索框与反馈入口，随抽屉高度一起上移。 */}
      <div
        ref={floatingOverlayRef}
        className={`absolute inset-x-3 z-[18] flex flex-col gap-2 ${
          isSheetDragging ? '' : 'transition-[bottom] duration-300 ease-out'
        }`}
        style={{ bottom: currentSheetHeight + 12 }}
      >
        {isAdmin ? (
          <div className="relative">
            {/* 搜索结果向上弹出，避免被下方抽屉遮挡。 */}
            {mapSearchResults.length > 0 || mapSearchMessage ? (
              <div
                onMouseDown={(event) => event.preventDefault()}
                className="absolute inset-x-0 bottom-full mb-2 max-h-[38vh] overflow-y-auto rounded-2xl border border-cyan-400/20 bg-zinc-900/95 p-1.5 shadow-[0_0_24px_rgba(34,211,238,0.12)] backdrop-blur-xl"
              >
                {mapSearchResults.length > 0 ? (
                  mapSearchResults.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleSelectMapSearchResult(result)}
                      className="flex w-full flex-col items-start gap-0.5 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-cyan-400/10"
                    >
                      <span className="text-sm text-white">{result.name}</span>
                      {result.address ? <span className="text-[11px] text-zinc-400">{result.address}</span> : null}
                    </button>
                  ))
                ) : (
                  <div className="px-2.5 py-2 text-xs text-zinc-400">{mapSearchMessage}</div>
                )}
              </div>
            ) : null}

            <div className="relative">
              <div aria-hidden className="pointer-events-none absolute -inset-2 rounded-[24px] backdrop-blur-md" style={SEARCH_BOX_BLUR_MASK_STYLE} />
              <div className="relative flex items-center gap-2 rounded-2xl border border-cyan-400/25 bg-zinc-900/95 px-3 py-2.5 text-zinc-200 backdrop-blur-xl">
                <MapPinned size={16} className="shrink-0 text-cyan-300/80" />
                <label className="flex min-w-0 flex-1 items-center">
                  <input
                    value={mapSearchKeyword}
                    onChange={(event) => {
                      const nextKeyword = event.target.value;
                      setMapSearchKeyword(nextKeyword);
                      if (!nextKeyword.trim()) {
                        setMapSearchResults([]);
                        setMapSearchMessage('');
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleMapSearch();
                      }
                    }}
                    placeholder="搜索地图地点（管理员）"
                    className="w-full min-w-0 bg-transparent text-sm text-white outline-none placeholder:text-zinc-500"
                  />
                </label>
                {mapSearchKeyword ? (
                  <button
                    onClick={handleResetMapSearch}
                    className="shrink-0 rounded-lg border border-white/10 px-2 py-1 text-[11px] text-zinc-300 transition-colors hover:border-cyan-300/40 hover:text-white"
                  >
                    清空
                  </button>
                ) : null}
                <button
                  onClick={handleMapSearch}
                  disabled={isMapSearchLoading || !mapSearchKeyword.trim()}
                  className="shrink-0 rounded-lg border border-cyan-400/30 px-2 py-1 text-[11px] text-cyan-200 transition-colors hover:border-cyan-300/50 hover:bg-cyan-400/15 disabled:opacity-40"
                >
                  {isMapSearchLoading ? '搜索中' : '搜索'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="pointer-events-none flex max-w-[70%] flex-col items-end gap-2 self-end">
          <div className="rounded-xl bg-black/55 px-2.5 py-1 text-right text-[11px] leading-relaxed text-cyan-100/80 backdrop-blur-sm">
            如有错误或想要追加标点请联系反馈
          </div>
        </div>

        <div className="relative">
          {/* 搜索框四周的模糊过渡层：四周留出一圈羽化带，让地图在搜索框边缘柔和淡出。 */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-2 rounded-[24px] backdrop-blur-md"
            style={SEARCH_BOX_BLUR_MASK_STYLE}
          />
          <div
            onClick={handleSearchBoxActivate}
            className="relative flex items-center gap-2 rounded-2xl border border-white/10 bg-zinc-900/95 px-3 py-2.5 text-zinc-200 backdrop-blur-xl"
          >
            <Search size={16} className="shrink-0 text-zinc-500" />
            <label className="flex min-w-0 flex-1 items-center">
              <input
                value={pointSearch}
                onChange={(event) => setPointSearch(event.target.value)}
                placeholder="搜索标点名称或说明"
                className="w-full min-w-0 bg-transparent text-sm text-white outline-none placeholder:text-zinc-500"
              />
            </label>
          </div>
        </div>

        <div className="pointer-events-none flex max-w-[70%] flex-col items-end gap-2 self-end">
          {!isAdmin && currentUser ? (
            <button
              onClick={() => {
                setIsSuggestionCardOpen(true);
                setSuggestionMessage('');
              }}
              className="pointer-events-auto inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-black/70 px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-cyan-200 shadow-[0_0_20px_rgba(34,211,238,0.12)] backdrop-blur-sm transition-colors hover:border-cyan-300/50 hover:bg-cyan-400/20"
            >
              <Plus size={14} />
              提交标点
            </button>
          ) : null}
        </div>
      </div>

      {(status !== 'ready' || isDataLoading) && (
        <div className="absolute inset-0 z-[30] flex items-center justify-center bg-black/65 px-6 text-center">
          <div className="max-w-md rounded-2xl border border-white/10 bg-black/70 px-5 py-4">
            <div className="font-orbitron text-sm uppercase tracking-[0.16em] text-cyan-300">
              {status === 'error' ? '地图暂不可用' : '地图初始化中'}
            </div>
            <div className="mt-2 text-sm leading-relaxed text-zinc-300">{overlayMessage}</div>
          </div>
        </div>
      )}

      {!isAdmin && currentUser && isSuggestionCardOpen ? (
        <div className="fixed inset-0 z-40 bg-black/45" onClick={() => setIsSuggestionCardOpen(false)}>
          <div className="absolute inset-0 flex items-center justify-center p-3 md:p-6">
            <div
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-[360px] rounded-3xl border border-cyan-400/20 bg-black/85 p-4 shadow-[0_0_30px_rgba(34,211,238,0.12)] backdrop-blur-md"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="font-orbitron text-sm uppercase tracking-[0.18em] text-cyan-200">
                  提交标点
                </div>
                <button
                  onClick={() => setIsSuggestionCardOpen(false)}
                  className="rounded-xl border border-white/10 px-3 py-1 text-xs text-zinc-300 transition-colors hover:border-cyan-300/40 hover:text-white"
                >
                  关闭
                </button>
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-cyan-400/15 bg-cyan-500/5 px-3 py-2 text-xs leading-relaxed text-cyan-100/80">
                  普通用户可提交想新增的标点地址、名字、类型和联系方式，等待管理员审核。联系方式为可选填写。
                </div>
                <label className="block text-xs font-orbitron uppercase tracking-[0.14em] text-zinc-400">
                  具体地址
                  <input
                    value={suggestionDraft.address}
                    onChange={(event) => setSuggestionDraft((previous) => ({ ...previous, address: event.target.value }))}
                    placeholder="请输入想要标点的具体地址"
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                  />
                </label>
                <label className="block text-xs font-orbitron uppercase tracking-[0.14em] text-zinc-400">
                  标点名字
                  <input
                    value={suggestionDraft.name}
                    onChange={(event) => setSuggestionDraft((previous) => ({ ...previous, name: event.target.value }))}
                    placeholder="请输入标点名字"
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                  />
                </label>
                <label className="block text-xs font-orbitron uppercase tracking-[0.14em] text-zinc-400">
                  联系方式（可选）
                  <input
                    value={suggestionDraft.contact}
                    onChange={(event) => setSuggestionDraft((previous) => ({ ...previous, contact: event.target.value }))}
                    placeholder="可填写微信、邮箱、手机号等"
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                  />
                </label>
                <label className="block text-xs font-orbitron uppercase tracking-[0.14em] text-zinc-400">
                  标点类型
                  <select
                    value={suggestionDraft.group}
                    onChange={(event) => setSuggestionDraft((previous) => ({ ...previous, group: event.target.value as KendamaMapPointGroup }))}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                  >
                    {POINT_GROUP_ORDER.map((group) => (
                      <option key={group} value={group} className="bg-black text-white">
                        {getPointGroupMeta(group).label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  onClick={() => void handleSubmitSuggestion()}
                  disabled={isSuggestionSubmitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-cyan-200 transition-colors hover:border-cyan-300/50 hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Plus size={14} />
                  {isSuggestionSubmitting ? '提交中' : '提交标点申请'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {selectedPoint && isPointCardOpen ? (
        <div className="fixed inset-0 z-40 bg-black/45" onClick={() => setIsPointCardOpen(false)}>
          <div className="absolute inset-0 flex items-center justify-center p-3 md:p-6">
            <div
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-[360px] rounded-3xl border border-cyan-400/20 bg-black/85 p-4 shadow-[0_0_30px_rgba(34,211,238,0.12)] backdrop-blur-md"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="font-orbitron text-sm uppercase tracking-[0.18em] text-cyan-200">
                  {isAdmin ? 'Point Editor' : 'Point Info'}
                </div>
                <button
                  onClick={() => setIsPointCardOpen(false)}
                  className="rounded-xl border border-white/10 px-3 py-1 text-xs text-zinc-300 transition-colors hover:border-cyan-300/40 hover:text-white"
                >
                  关闭
                </button>
              </div>

              {isAdmin ? (
                <div className="space-y-3">
                  <label className="block text-xs font-orbitron uppercase tracking-[0.14em] text-zinc-400">
                    名称
                    <input
                      value={draft.name}
                      onChange={(event) => setDraft((previous) => ({ ...previous, name: event.target.value }))}
                      className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                    />
                  </label>
                  <label className="block text-xs font-orbitron uppercase tracking-[0.14em] text-zinc-400">
                    说明
                    <textarea
                      rows={4}
                      value={draft.description}
                      onChange={(event) => setDraft((previous) => ({ ...previous, description: event.target.value }))}
                      className="mt-2 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white outline-none transition-colors focus:border-cyan-400/40"
                    />
                  </label>
                  <div className="rounded-2xl border border-white/10 bg-black/35 p-3">
                    <div className="text-xs font-orbitron uppercase tracking-[0.14em] text-zinc-400">
                      标点 Logo
                    </div>
                    <div className="mt-3 flex items-start gap-3">
                      <div
                        className={`flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-[20px] border p-3 shadow-[0_0_24px_rgba(255,255,255,0.08)] ${getPointLogoSurfaceClassName(draft.logoBackground)}`}
                      >
                        {draft.logoImageUrl ? (
                          <img src={draft.logoImageUrl} alt="Point logo preview" className="h-full w-full rounded-2xl object-contain" />
                        ) : (
                          <div className="text-center text-[11px] leading-relaxed text-zinc-500">暂无 Logo</div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 space-y-2">
                        <label className="inline-flex cursor-pointer items-center justify-center rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-cyan-200 transition-colors hover:border-cyan-300/50 hover:bg-cyan-400/20">
                          上传 Logo
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            onChange={(event) => void handleLogoFileChange(event)}
                            className="hidden"
                          />
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {(['black', 'white'] as KendamaMapPointLogoBackground[]).map((background) => {
                            const isActive = draft.logoBackground === background;
                            return (
                              <button
                                key={background}
                                type="button"
                                onClick={() => setDraft((previous) => ({ ...previous, logoBackground: background }))}
                                className={`rounded-xl border px-3 py-2 text-[11px] font-orbitron uppercase tracking-[0.14em] transition-colors ${
                                  isActive
                                    ? 'border-cyan-300/50 bg-cyan-500/15 text-cyan-100'
                                    : 'border-white/10 bg-black/40 text-zinc-400 hover:border-cyan-400/30 hover:text-cyan-200'
                                }`}
                              >
                                {background === 'black' ? '黑底' : '白底'}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveDraftLogo}
                          disabled={!draft.logoImageUrl}
                          className="inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-[11px] font-orbitron uppercase tracking-[0.14em] text-red-200 transition-colors hover:border-red-300/50 hover:bg-red-400/20 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Trash2 size={12} />
                          清除 Logo
                        </button>
                        <div className="text-[11px] leading-relaxed text-zinc-500">
                          支持 PNG、JPG、WEBP、SVG，建议上传透明底方图，文件不超过 1MB。图标会以圆角卡片样式显示。
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleApplyDraft}
                      className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-cyan-200 transition-colors hover:border-cyan-300/50 hover:bg-cyan-400/20"
                    >
                      <PencilLine size={14} />
                      应用到当前点位
                    </button>
                    <button
                      onClick={handleDeleteSelectedPoint}
                      disabled={points.length <= 1 || isSaving}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs font-orbitron uppercase tracking-[0.14em] text-red-200 transition-colors hover:border-red-300/50 hover:bg-red-400/20 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Trash2 size={14} />
                      删除当前点位
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div
                    className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] ${getPointGroupMeta(selectedPoint.group).chipClassName}`}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{
                        backgroundColor: getPointGroupMeta(selectedPoint.group).markerColor,
                        boxShadow: `0 0 10px ${getPointGroupMeta(selectedPoint.group).markerGlow}`,
                      }}
                    />
                    <span>{getPointGroupMeta(selectedPoint.group).label}</span>
                  </div>
                  {selectedPoint.logo ? (
                    <div className="flex justify-start">
                      <div
                        className={`flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-[22px] border p-3 shadow-[0_0_24px_rgba(255,255,255,0.08)] ${getPointLogoSurfaceClassName(selectedPoint.logo.background)}`}
                      >
                        <img src={selectedPoint.logo.imageUrl} alt={`${selectedPoint.name} logo`} className="h-full w-full rounded-2xl object-contain" />
                      </div>
                    </div>
                  ) : null}
                  <div className="text-lg font-semibold text-white">{selectedPoint.name}</div>
                  <div className="text-sm leading-relaxed text-zinc-300">
                    {selectedPoint.description || '该点位暂未填写说明。'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
